import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { LOCATIONS, COURSES, SITE, GOOGLE_REVIEWS } from "@/site";
import SEO, { localBusinessSchema, breadcrumbSchema } from "@/components/SEO";
import CTABanner from "@/components/sections/CTABanner";
import LeadFormCompact from "@/components/sections/LeadFormCompact";
import Breadcrumbs from "@/components/Breadcrumbs";
import { MapPin, Phone, Mail } from "lucide-react";

export default function LocationPage() {
    const { slug } = useParams();
    const loc = LOCATIONS.find((l) => l.slug === slug);
    if (!loc) return <Navigate to="/" replace />;

    const schema = [
        localBusinessSchema(),
        breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Locations", path: "/locations" },
            { name: loc.name, path: `/locations/${loc.slug}` },
        ]),
    ];

    return (
        <div data-testid={`page-location-${slug}`}>
            <SEO title={loc.title} description={loc.metaDesc} path={`/locations/${loc.slug}`} schema={schema} />
            <Breadcrumbs items={[
                { label: "Home", to: "/" },
                { label: "Locations" },
                { label: loc.name },
            ]} />

            <section className="relative py-24 md:py-32 bg-black overflow-hidden">
                <div className="absolute inset-0 bg-radial-gold opacity-60" aria-hidden />
                <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/90">Location · {loc.name}</span>
                    <h1 className="mt-3 font-display text-4xl md:text-6xl font-bold text-white leading-[1.05]">{loc.h1}</h1>
                    <p className="mt-5 text-white/70 text-base md:text-lg max-w-3xl leading-relaxed">{loc.intro}</p>
                    <p className="mt-3 text-sm text-gold">{loc.distance}</p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <Link to="/contact" data-testid="location-book-demo-btn" className="inline-flex items-center justify-center bg-gold text-black font-semibold text-sm px-8 py-4 rounded-full hover:scale-[1.03] hover:bg-gold-hover transition-all duration-300 gold-glow-strong">Book Free Trial Class</Link>
                        <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center border border-white/25 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/5 transition-colors">Chat on WhatsApp</a>
                    </div>
                </div>
            </section>

            {/* COURSES AVAILABLE */}
            <section className="relative py-20 md:py-28 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-10">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Available to {loc.name} residents</span>
                        <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-white leading-tight">All 12 courses — a short drive away.</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {COURSES.map((c) => (
                            <Link key={c.slug} to={`/courses/${c.slug}`} className="rounded-xl border border-white/10 bg-[#121212] p-5 hover:border-gold/40 transition-colors">
                                <div className="text-gold text-[10px] uppercase tracking-[0.2em]">{c.short}</div>
                                <div className="mt-2 font-display text-lg text-white font-semibold">{c.name}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* REACH US */}
            <section className="relative py-20 md:py-28 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                    <div>
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Reach us from {loc.name}</span>
                        <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-white leading-tight">Studio address & directions</h2>
                        <ul className="mt-8 space-y-5 text-sm">
                            <li className="flex items-start gap-3 text-white/80"><MapPin className="w-4 h-4 text-gold mt-1" /><span>{SITE.address}</span></li>
                            <li className="flex items-center gap-3 text-white/80"><Phone className="w-4 h-4 text-gold" /><a href={`tel:${SITE.phoneTel}`}>{SITE.phoneIntl}</a></li>
                            <li className="flex items-center gap-3 text-white/80"><Mail className="w-4 h-4 text-gold" /><a href={`mailto:${SITE.email}`} className="break-all">{SITE.email}</a></li>
                        </ul>
                        <p className="mt-8 text-white/60 leading-relaxed">We're easy to reach from {loc.name} — right opposite Select Citywalk / DLF Avenue Saket. Street parking and nearby metro access make the commute simple for weekly classes.</p>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-white/10 min-h-[360px]">
                        <iframe title={`Ihao Music House map for ${loc.name}`} src={SITE.mapEmbed} className="w-full h-full min-h-[360px] grayscale contrast-125" style={{ border: 0 }} loading="lazy" />
                    </div>
                </div>
            </section>

            {/* GOOGLE REVIEWS */}
            <section data-testid="google-reviews" className="relative py-20 md:py-28 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.3em] uppercase text-gold/80">
                                    <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3A12 12 0 0 1 12 24a12 12 0 0 1 19.7-9.3l5.7-5.7A20 20 0 1 0 44 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12a12 12 0 0 1 7.7 2.7l5.7-5.7A20 20 0 0 0 24 4a20 20 0 0 0-17.7 10.7z"/><path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.5-5.2l-6.2-5.3A12 12 0 0 1 24 36a12 12 0 0 1-11.3-7.9l-6.5 5A20 20 0 0 0 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.5l6.2 5.3C41.4 35 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
                                    Google Reviews
                                </span>
                            </div>
                            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
                                What {loc.name} students say about us.
                            </h2>
                            <div className="mt-3 flex items-center gap-3">
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: 5 }).map((_, j) => (
                                        <svg key={j} viewBox="0 0 20 20" className="w-4 h-4 fill-[#FBBC04]"><path d="M10 1l2.8 6.1 6.7.6-5 4.5 1.5 6.5L10 15.3 3.9 18.7l1.5-6.5-5-4.5 6.7-.6z"/></svg>
                                    ))}
                                </div>
                                <span className="text-white/80 text-sm font-semibold">4.9 / 5</span>
                                <span className="text-white/40 text-xs">· based on 120+ Google reviews</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {GOOGLE_REVIEWS.map((r, i) => (
                            <div key={i} data-testid={`google-review-${i}`} className="rounded-2xl border border-white/10 bg-[#121212] p-7 hover:border-gold/40 transition-colors">
                                <div className="flex items-center gap-3">
                                    <span className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center font-display font-semibold text-sm">
                                        {r.initials}
                                    </span>
                                    <div>
                                        <div className="text-white font-semibold text-sm">{r.name}</div>
                                        <div className="flex items-center gap-1 mt-0.5">
                                            {Array.from({ length: r.rating }).map((_, j) => (
                                                <svg key={j} viewBox="0 0 20 20" className="w-3 h-3 fill-[#FBBC04]"><path d="M10 1l2.8 6.1 6.7.6-5 4.5 1.5 6.5L10 15.3 3.9 18.7l1.5-6.5-5-4.5 6.7-.6z"/></svg>
                                            ))}
                                            <span className="text-[10px] text-white/40 ml-1">· {r.date}</span>
                                        </div>
                                    </div>
                                </div>
                                <p className="mt-5 text-sm text-white/75 leading-relaxed">"{r.quote}"</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 text-center">
                        <a href="https://maps.google.com/?q=Ihao+Music+House+Delhi" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-gold hover:text-gold-hover border-b border-gold/40 pb-0.5">
                            Read more reviews on Google →
                        </a>
                    </div>
                </div>
            </section>

            <LeadFormCompact />

            <CTABanner title={`Nearest premium music academy to ${loc.name}.`} subtitle="Book a free trial class and experience the Ihao difference for yourself." />
        </div>
    );
}
